import { useState } from "react";
import { Alert, Box, CircularProgress, Typography } from "@mui/material";
import { Button, PillTextField } from "../../components/ui";
import { useThemeColors } from "../../hooks/useThemeColors";
import {
  ReviewItem,
  ReportItem,
  TaskItem,
  approveItem,
  approveTask,
  isReviewable,
  openReportPdf,
  rejectItem,
  rejectTask,
  requestRevision,
} from "./jobCardApi";
import { REJECT_TONE, REVISION_TONE, errorMessage, money, tone } from "./jobCardShared";

type Mode = "idle" | "approve" | "revision" | "reject";

const useRunner = (onDone: () => void) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async (fn: () => Promise<unknown>, failure: string) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      onDone();
    } catch (e) {
      setError(errorMessage(e, failure));
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, setError, run };
};

const Note = ({ children }: { children: React.ReactNode }) => {
  const colors = useThemeColors();
  return (
    <Typography sx={{ fontSize: "0.85rem", color: colors.primary[300] }}>{children}</Typography>
  );
};

const Spinner = () => <CircularProgress size={16} sx={{ color: "inherit" }} />;

// ------------------------------------------------- proposals / assignments --

export const ApprovalPanel = ({ item, onDone }: { item: ReviewItem; onDone: () => void }) => {
  const colors = useThemeColors();
  const { busy, error, setError, run } = useRunner(onDone);
  const [mode, setMode] = useState<Mode>("idle");
  const [hours, setHours] = useState(item.hours !== null ? String(item.hours) : "");
  const [reward, setReward] = useState(String(item.reward));
  const [text, setText] = useState("");
  const [suggested, setSuggested] = useState("");

  if (!isReviewable(item)) {
    return (
      <Note>
        {item.status === "approved" || item.status === "rejected"
          ? `This ${item.kind} has already been ${item.status}.`
          : `Nothing to review yet. It is currently ${item.status.replace("_", " ")}.`}
      </Note>
    );
  }

  const hourBased = item.hours !== null;
  const hoursNum = Number(hours);
  const estimated =
    hourBased && item.hourlyRate !== null && hours !== "" && !Number.isNaN(hoursNum)
      ? hoursNum * item.hourlyRate
      : null;

  const back = () => {
    setMode("idle");
    setError(null);
  };

  const confirm = () => {
    if (mode === "approve") {
      if (hourBased ? hours === "" || hoursNum < 0 : reward === "" || Number(reward) < 0) {
        setError(hourBased ? "Enter the approved hours." : "Enter the approved reward.");
        return;
      }
      run(
        () =>
          approveItem(item, {
            hours: hourBased ? hoursNum : null,
            reward: hourBased ? null : Number(reward),
            message: text.trim(),
          }),
        "Could not approve this item.",
      );
    } else if (mode === "revision") {
      if (!text.trim()) return setError("Feedback is required.");
      run(
        () => requestRevision(item, text.trim(), suggested === "" ? null : Number(suggested)),
        "Could not request a revision.",
      );
    } else if (mode === "reject") {
      if (!text.trim()) return setError("A rejection reason is required.");
      run(() => rejectItem(item, text.trim()), "Could not reject this item.");
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {mode === "idle" && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
          <Button onClick={() => setMode("approve")}>Approve</Button>
          <Button
            onClick={() => setMode("revision")}
            sx={tone(REVISION_TONE.bg, REVISION_TONE.fg)}
          >
            Request revision
          </Button>
          <Button onClick={() => setMode("reject")} sx={tone(REJECT_TONE.bg, REJECT_TONE.fg)}>
            Reject
          </Button>
        </Box>
      )}

      {mode === "approve" && (
        <>
          {hourBased ? (
            <>
              <PillTextField
                label="Approved hours"
                type="number"
                value={hours}
                onChange={(e) => setHours(e.target.value)}
                inputProps={{ min: 0, step: 0.25 }}
              />
              {estimated !== null && (
                <Typography sx={{ fontSize: "0.75rem", color: colors.primary[300] }}>
                  Pays about {money(estimated)} at the intern&apos;s current rate.
                </Typography>
              )}
            </>
          ) : (
            <PillTextField
              label="Approved reward ($)"
              type="number"
              value={reward}
              onChange={(e) => setReward(e.target.value)}
              inputProps={{ min: 0, step: 0.01 }}
            />
          )}
          <PillTextField
            label="Message (optional)"
            multiline
            minRows={2}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </>
      )}

      {mode === "revision" && (
        <>
          <PillTextField
            label="What needs to change?"
            multiline
            minRows={3}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          <PillTextField
            label="Suggested reward (optional)"
            type="number"
            value={suggested}
            onChange={(e) => setSuggested(e.target.value)}
            inputProps={{ min: 0, step: 0.01 }}
          />
        </>
      )}

      {mode === "reject" && (
        <PillTextField
          label="Reason for rejection"
          multiline
          minRows={3}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
      )}

      {error && <Alert severity="error">{error}</Alert>}

      {mode !== "idle" && (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button disabled={busy} onClick={back} sx={tone("transparent", colors.primary[100])}>
            Back
          </Button>
          <Button onClick={confirm} disabled={busy} sx={{ ml: "auto" }}>
            {busy ? (
              <Spinner />
            ) : mode === "approve" ? (
              "Confirm approval"
            ) : mode === "revision" ? (
              "Send request"
            ) : (
              "Confirm rejection"
            )}
          </Button>
        </Box>
      )}
    </Box>
  );
};

// ------------------------------------------------------------------ tasks --

export const TaskPanel = ({ task, onDone }: { task: TaskItem; onDone: () => void }) => {
  const colors = useThemeColors();
  const { busy, error, setError, run } = useRunner(onDone);
  const [mode, setMode] = useState<Mode>("idle");
  const [hours, setHours] = useState(String(task.hoursLogged));
  const [reason, setReason] = useState("");

  if (task.status !== "pending") {
    return <Note>This task has already been {task.status}.</Note>;
  }

  const back = () => {
    setMode("idle");
    setError(null);
  };

  const confirm = () => {
    if (mode === "approve") {
      const n = Number(hours);
      if (hours === "" || Number.isNaN(n) || n < 0) return setError("Enter the approved hours.");
      run(() => approveTask(task, n), "Could not approve this task.");
    } else if (mode === "reject") {
      if (!reason.trim()) return setError("A rejection reason is required.");
      run(() => rejectTask(task, reason.trim()), "Could not reject this task.");
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {mode === "idle" && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
          <Button onClick={() => setMode("approve")}>Approve</Button>
          <Button onClick={() => setMode("reject")} sx={tone(REJECT_TONE.bg, REJECT_TONE.fg)}>
            Reject
          </Button>
        </Box>
      )}

      {mode === "approve" && (
        <PillTextField
          label="Approved hours"
          type="number"
          value={hours}
          onChange={(e) => setHours(e.target.value)}
          inputProps={{ min: 0, step: 0.25 }}
        />
      )}
      {mode === "reject" && (
        <PillTextField
          label="Reason for rejection"
          multiline
          minRows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      )}

      {error && <Alert severity="error">{error}</Alert>}

      {mode !== "idle" && (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button disabled={busy} onClick={back} sx={tone("transparent", colors.primary[100])}>
            Back
          </Button>
          <Button onClick={confirm} disabled={busy} sx={{ ml: "auto" }}>
            {busy ? <Spinner /> : mode === "approve" ? "Confirm approval" : "Confirm rejection"}
          </Button>
        </Box>
      )}
    </Box>
  );
};

// ---------------------------------------------------------------- reports --

export const ReportPanel = ({ report }: { report: ReportItem }) => {
  const colors = useThemeColors();
  const [error, setError] = useState<string | null>(null);
  const [pdfBusy, setPdfBusy] = useState(false);

  const viewPdf = async () => {
    setPdfBusy(true);
    setError(null);
    try {
      await openReportPdf(report.id);
    } catch (e) {
      setError(errorMessage(e, "Could not open the PDF."));
    } finally {
      setPdfBusy(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
      <Button
        onClick={viewPdf}
        disabled={pdfBusy}
        sx={tone(colors.primary[800], colors.primary[100])}
      >
        {pdfBusy ? <Spinner /> : "View PDF"}
      </Button>

      {report.status === "finalised" && <Note>This report is finalised and locked.</Note>}
      {error && <Alert severity="error">{error}</Alert>}
    </Box>
  );
};
